import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * Per-pane tab-SELECTION state -- which tab's `contentKey` is currently
 * selected within ONE tab strip (`DocumentTitleNav`/`ReviewTransactionsTabs`), and the setter that
 * strip's now-controlled Chakra `<Tabs index onChange>` calls. Deliberately NOT part of
 * `CodingScreenConfig` (config is presentation data; this is interaction state) and NOT part of
 * `interface/state.ts` (that's the type-only bridge to the parent app's redux/Apollo state; tab
 * selection is local, ephemeral UI state that never needs to leave this folder). A
 * `TabSelectionProvider` is mounted ONCE PER PANE -- `organisms/DocumentationPane` and
 * `organisms/ReviewTransactionsPane` each wrap their own tab-strip + body subtree in their own
 * provider -- so the left and right tab strips each get an independent selection, never sharing one.
 *
 * NON-THROWING DEFAULT (unlike `interface/context.tsx`'s `useCodingScreen`, which throws outside its
 * provider): `createContext` is seeded with a plain `{ selectedContentKey: undefined, select: () =>
 * {} }` object, so `useTabSelection()` is always safe to call -- a tab molecule (or the body molecule
 * that reads the selection to pick which slot to render) mounted outside any pane -- an isolated
 * story, or a unit test that doesn't care about selection -- still renders correctly, just with
 * nothing ever selected and a `select()` that is a harmless no-op instead of a hard crash. This
 * mirrors `registry.tsx`'s `useSlotRegistry()` defaulting to `EMPTY_SLOT_REGISTRY` outside
 * `SlotRegistryProvider`, not `interface/context.tsx`'s throwing `useCodingScreen()`.
 *
 * Isolation: imports ONLY `react` -- nothing else from `project_starburst` or beyond, trivially
 * satisfying the boundary guard (`../../__tests__/boundary.test.ts`).
 */
export interface TabSelectionContextValue {
  selectedContentKey: string | undefined;
  select: (contentKey: string) => void;
}

/** Module-level singleton (not re-created per render) -- the exact non-throwing default every
 *  `useTabSelection()` call outside a provider reads back verbatim. */
const DEFAULT_TAB_SELECTION: TabSelectionContextValue = {
  selectedContentKey: undefined,
  select: () => {}
};

const Ctx = createContext<TabSelectionContextValue>(DEFAULT_TAB_SELECTION);

/**
 * `defaultContentKey` seeds the initial `selectedContentKey` via `useState`'s initializer -- the
 * caller (`DocumentationPane`/`ReviewTransactionsPane`) computes its pane's first-visible-tab
 * `contentKey` and passes it here; omitting it (or passing `undefined`, the empty-shell default when
 * a pane has no tabs at all) leaves nothing selected. `select` is threaded straight through as the
 * raw `useState` setter -- a caller passing a plain `string` (every real call site: a controlled
 * `<Tabs onChange={(i) => select(visibleTabs[i].contentKey)}>`) is exactly what `Dispatch<SetStateAction<...>>`
 * accepts, so no wrapper function is needed.
 *
 * RE-SEED ON DEFAULT CHANGE: a `useState` initializer only runs on
 * first mount -- if `defaultContentKey` starts `undefined` (tabs not yet resolved from config) and a
 * real value arrives on a LATER render, the initializer never sees it and `selectedContentKey` would
 * stay stuck at `undefined` forever. The `useEffect` below re-seeds `selectedContentKey` whenever
 * `defaultContentKey` changes, but ONLY when nothing has been explicitly selected yet (current
 * `selectedContentKey === undefined`) -- it must never clobber a real user click. When
 * `defaultContentKey` stays `undefined` (the empty shell, no tabs at all) the effect's own
 * `setSelected` call is a same-value no-op, so it does not cause a re-render loop.
 */
export const TabSelectionProvider: React.FC<
  React.PropsWithChildren<{ defaultContentKey?: string }>
> = ({ defaultContentKey, children }) => {
  const [selectedContentKey, setSelected] = useState<string | undefined>(
    defaultContentKey
  );

  useEffect(() => {
    setSelected((prev) => (prev === undefined ? defaultContentKey : prev));
  }, [defaultContentKey]);

  return (
    <Ctx.Provider value={{ selectedContentKey, select: setSelected }}>
      {children}
    </Ctx.Provider>
  );
};

/** Reads the nearest `TabSelectionProvider`'s value, or the non-throwing `DEFAULT_TAB_SELECTION`
 *  above when none is present -- see this file's header comment for why that default doesn't throw. */
export function useTabSelection(): TabSelectionContextValue {
  return useContext(Ctx);
}
